// packages/constants/src/topMovies.js
//
// My Top Movies ranking rules, shared by web and mobile (pure functions; each
// client persists the draft its own way — localStorage / AsyncStorage).
//
//   picks     the ranking itself, in order, at most `size` movies
//   setAside  movies moved out when switching Top 10 → Top 5. They are kept,
//             not deleted, and come back when switching to Top 10 again.

export const TOP_MOVIES_SIZES = [5, 10];
export function emptyDraft() {
    return { size: 5, ranked: true, picks: [], setAside: [], pendingSave: false };
}

/** The fields a ranking needs from a search result or a saved movie. */
export function toMovie(media) {
    return {
        tmdbId: Number(media.tmdbId ?? media.id),
        title: media.title || 'Untitled',
        year: media.year ?? null,
        posterUrl: media.posterUrl ?? null,
    };
}

export const isComplete = (draft) => draft.picks.length === draft.size;
export const hasMovie = (draft, tmdbId) => draft.picks.some((m) => m.tmdbId === tmdbId) || draft.setAside.some((m) => m.tmdbId === tmdbId);

/**
 * Add a movie at the end of the ranking.
 * status: 'added' | 'restored' (it was set aside) | 'duplicate' | 'full'
 */
export function addMovie(draft, media) {
    const movie = toMovie(media);
    if (draft.picks.some((m) => m.tmdbId === movie.tmdbId)) return { draft, status: 'duplicate' };
    if (draft.picks.length >= draft.size) return { draft, status: 'full' };
    const wasSetAside = draft.setAside.some((m) => m.tmdbId === movie.tmdbId);
    return {
        draft: { ...draft, picks: [...draft.picks, movie], setAside: draft.setAside.filter((m) => m.tmdbId !== movie.tmdbId) },
        status: wasSetAside ? 'restored' : 'added',
    };
}

export function removeMovie(draft, tmdbId) {
    return { ...draft, picks: draft.picks.filter((m) => m.tmdbId !== tmdbId) };
}

/** Move the movie at `from` to `to` (drag-and-drop or the up/down buttons). */
export function moveMovie(draft, from, to) {
    if (from === to || from < 0 || to < 0 || from >= draft.picks.length || to >= draft.picks.length) return draft;
    const picks = [...draft.picks];
    const [moved] = picks.splice(from, 1);
    picks.splice(to, 0, moved);
    return { ...draft, picks };
}

/** How many picks a switch to `size` would set aside (0 = nothing changes). */
export const overflowFor = (draft, size) => Math.max(0, draft.picks.length - size);

/**
 * Switch between Top 5 and Top 10. Shrinking never deletes: the extra picks
 * (ranked 6+) move to `setAside`. Growing brings set-aside movies back first.
 */
export function setSize(draft, size) {
    if (!TOP_MOVIES_SIZES.includes(size) || size === draft.size) return draft;
    if (size < draft.size) {
        return { ...draft, size, picks: draft.picks.slice(0, size), setAside: [...draft.picks.slice(size), ...draft.setAside] };
    }
    const picks = [...draft.picks, ...draft.setAside].slice(0, size);
    const kept = new Set(picks.map((m) => m.tmdbId));
    return { ...draft, size, picks, setAside: draft.setAside.filter((m) => !kept.has(m.tmdbId)) };
}

export const discardSetAside = (draft) => ({ ...draft, setAside: [] });
export const setRanked = (draft, ranked) => ({ ...draft, ranked: Boolean(ranked) });

/** Editor state from a saved list (GET /top-movies/me). */
export function draftFromSaved(saved) {
    if (!saved) return emptyDraft();
    return { size: saved.size, ranked: saved.ranked, picks: saved.movies.map(toMovie), setAside: [], pendingSave: false };
}

/** True when the editor holds exactly what is saved (nothing to save). */
export function matchesSaved(draft, saved) {
    if (!saved) return false;
    return draft.size === saved.size
        && draft.ranked === saved.ranked
        && draft.picks.length === saved.movies.length
        && draft.picks.every((m, i) => m.tmdbId === saved.movies[i].tmdbId);
}

export const TOP_MOVIES_DRAFT_STORAGE_KEY = 'aluvo_top_movies_draft';

/** A parsed draft from storage, normalised; null when missing or corrupt. */
export function normalizeStoredDraft(parsed) {
    if (!parsed || !TOP_MOVIES_SIZES.includes(parsed.size) || !Array.isArray(parsed.picks) || !Array.isArray(parsed.setAside)) return null;
    return {
        ...emptyDraft(),
        ...parsed,
        picks: parsed.picks.slice(0, parsed.size).map(toMovie),
        setAside: parsed.setAside.slice(0, 10).map(toMovie),
    };
}

/**
 * Drag-and-drop: the slot a row dragged from `from` lands in after moving
 * `offset` pixels, with rows `pitch` pixels apart (row height + gap). Rounds
 * to the nearest row and stays inside the `count` filled slots.
 */
export function targetIndexFor(from, offset, pitch, count) {
    if (!pitch || !Number.isFinite(offset)) return from;
    return Math.max(0, Math.min(count - 1, from + Math.round(offset / pitch)));
}
